import { useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/auth/AuthContext';
import { invites as invitesApi } from '@/api/invites';
import { ApiError } from '@/api/client';
import ErrorBanner from '@/components/ErrorBanner';
import Spinner from '@/components/Spinner';

/**
 * Two cases:
 *   - Not signed in → redirect to /signup?inviteToken=... so they can register and the
 *     server auto-accepts the invite during register.
 *   - Already signed in (and the email matches) → show "Accept" button that POSTs to
 *     /api/invites/{token}/accept.
 */
export default function InvitePage() {
  const { token } = useParams<{ token: string }>();
  const { isAuthenticated, isLoading, user, refresh } = useAuth();
  const navigate = useNavigate();

  const preview = useQuery({
    queryKey: ['invite', token],
    queryFn: () => invitesApi.preview(token!),
    enabled: !!token,
    retry: false,
  });

  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<unknown>(null);

  if (!token) return <Navigate to="/login" replace />;

  if (isLoading || preview.isLoading) {
    return (
      <div className="centered-full">
        <Spinner />
      </div>
    );
  }

  if (preview.isError) {
    return (
      <div className="centered-full login-card">
        <div className="login-inner">
          <h1>Invite expired</h1>
          <p className="muted">This invite link is invalid or has expired.</p>
          <Link to="/login" className="btn btn-ghost">
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  // Not signed in → bounce to signup with the invite token. The signup form pre-fills email.
  if (!isAuthenticated) {
    return <Navigate to={`/signup?inviteToken=${encodeURIComponent(token)}`} replace />;
  }

  const data = preview.data!;
  const emailMismatch = user!.email.toLowerCase() !== data.email.toLowerCase();

  const handleAccept = async () => {
    setAccepting(true);
    setError(null);
    try {
      await invitesApi.accept(token);
      await refresh();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setError(new Error('The invite was sent to a different email address.'));
      } else {
        setError(err);
      }
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div className="centered-full login-card">
      <div className="login-inner">
        <h1>Join {data.organizationName}</h1>
        <p className="muted">
          You were invited as <strong>{data.role.toLowerCase()}</strong>.
        </p>
        {error ? <ErrorBanner error={error} /> : null}
        {emailMismatch ? (
          <p className="muted">
            This invite was sent to <strong>{data.email}</strong>, but you&apos;re signed in as{' '}
            <strong>{user!.email}</strong>. Sign out and accept the invite using the correct
            account.
          </p>
        ) : (
          <button
            type="button"
            className="btn btn-primary"
            disabled={accepting}
            onClick={handleAccept}
          >
            {accepting ? 'Accepting…' : `Accept invite to ${data.organizationName}`}
          </button>
        )}
      </div>
    </div>
  );
}
